import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { LocateFixed, MapPin } from "lucide-react";
import { loadGoogleMapsLibrary, type GoogleMapsMap, type GoogleMapsLatLngBounds } from "../../lib/googleMaps";
import { displayTime, mapRecords, type DutyHistory } from "./types";

export default function DutyHistoryMap({ history, selectedId, onSelect, getMapConfig }: {
  history: DutyHistory; selectedId: string | null; onSelect: (id: string) => void; getMapConfig: () => Promise<{ api_key: string }>;
}) {
  const host = useRef<HTMLDivElement>(null);
  const map = useRef<GoogleMapsMap | null>(null);
  const bounds = useRef<GoogleMapsLatLngBounds | null>(null);
  const markerNodes = useRef<{ ids: string[]; node: HTMLButtonElement }[]>([]);
  const selectRef = useRef(onSelect);
  selectRef.current = onSelect;
  const [setupError, setSetupError] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const [authError, setAuthError] = useState(false);
  useEffect(() => {
    const globals = window as Window & { gm_authFailure?: () => void };
    const previous = globals.gm_authFailure;
    const failed = () => { setAuthError(true); previous?.(); };
    globals.gm_authFailure = failed;
    return () => { if (globals.gm_authFailure === failed) globals.gm_authFailure = previous; };
  }, []);
  const records = useMemo(() => mapRecords(history), [history]);
  const config = useQuery({ queryKey: ["google-maps-configuration"], queryFn: getMapConfig, enabled: records.length > 0, staleTime: Infinity, retry: false });
  const library = useQuery({ queryKey: ["google-maps-map-library"], queryFn: () => loadGoogleMapsLibrary(config.data!.api_key), enabled: records.length > 0 && Boolean(config.data?.api_key), staleTime: Infinity, retry: false });
  useEffect(() => {
    const element = host.current;
    if (!element || !library.data || !records.length) return;
    const maps = library.data;
    const overlays: { setMap: (value: null) => void }[] = [];
    let resize: ResizeObserver | undefined;
    setSetupError(false);
    try {
      const instance = new maps.Map(element, { center: { lat: records[0].latitude, lng: records[0].longitude }, zoom: 16, mapTypeControl: false, streetViewControl: false, fullscreenControl: false, gestureHandling: "cooperative" });
      map.current = instance;
      const extent = new maps.LatLngBounds();
      const groups = new Map<string, typeof records>();
      records.forEach(record => {
        extent.extend({ lat: record.latitude, lng: record.longitude });
        const key = `${record.latitude.toFixed(6)}:${record.longitude.toFixed(6)}`;
        groups.set(key, [...(groups.get(key) ?? []), record]);
      });
      bounds.current = extent;
      markerNodes.current = [];
      groups.forEach(group => {
        const record = group.find(item => item.action) ?? group[0];
        const node = document.createElement("button");
        node.type = "button";
        node.className = `dh-marker${record.action ? " dh-marker-action" : ""}`;
        node.textContent = record.action ? record.label : "";
        node.title = group.map(item => `${item.label} · ${displayTime(item.time, history.time_zone)}`).join("\n");
        node.setAttribute("aria-label", node.title);
        node.addEventListener("click", event => { event.stopPropagation(); selectRef.current(record.id); });
        const overlay = new maps.OverlayView();
        overlay.onAdd = () => overlay.getPanes()?.overlayMouseTarget.appendChild(node);
        overlay.draw = () => {
          const point = overlay.getProjection()?.fromLatLngToDivPixel(new maps.LatLng(record.latitude, record.longitude));
          if (point) { node.style.left = `${point.x}px`; node.style.top = `${point.y}px`; }
        };
        overlay.onRemove = () => node.remove();
        overlay.setMap(instance); overlays.push(overlay);
        markerNodes.current.push({ ids: group.map(item => item.id), node });
      });
      // Deliberately do not draw a route: samples do not prove the path between them.
      const fit = () => {
        if (groups.size === 1) { instance.setCenter({ lat: records[0].latitude, lng: records[0].longitude }); instance.setZoom(16); }
        else instance.fitBounds(extent, 65);
      };
      fit();
      resize = new ResizeObserver(entries => { if (entries[0]?.contentRect.width && entries[0].contentRect.height) fit(); });
      resize.observe(element);
    } catch { setSetupError(true); }
    return () => {
      resize?.disconnect(); overlays.forEach(overlay => overlay.setMap(null)); markerNodes.current = [];
      map.current = null; bounds.current = null; element.replaceChildren();
    };
  }, [library.data, records, history.time_zone, attempt]);
  const selected = records.find(record => record.id === selectedId);
  useEffect(() => {
    markerNodes.current.forEach(({ ids, node }) => node.setAttribute("aria-pressed", String(Boolean(selectedId && ids.includes(selectedId)))));
    if (selected && map.current) { map.current.setCenter({ lat: selected.latitude, lng: selected.longitude }); map.current.setZoom(Math.max(map.current.getZoom() ?? 16, 16)); }
  }, [selected, selectedId, library.data]);
  const failed = authError || setupError || config.isError || library.isError || (config.isSuccess && !config.data.api_key);
  return <div className="dh-map-section">
    <div className="dh-map-toolbar"><span><MapPin size={15} />Confirmed GPS only</span>
      <button type="button" className="dh-button" disabled={!records.length || !library.data || Boolean(failed)} onClick={() => { if (map.current && bounds.current) { map.current.fitBounds(bounds.current, 65); if (records.length < 3) map.current.setZoom(16); } }}><LocateFixed size={15} />Show all</button>
    </div>
    <div className="dh-map-wrap">
      <div ref={host} className="dh-map" aria-label={`Recorded locations for ${history.user_name} on ${history.date}`} />
      {!records.length ? <div className="dh-map-state"><MapPin size={28} /><h3>No GPS recorded</h3><p>There are no confirmed device locations for this user on this day. Recorded actions are listed in the timeline.</p></div>
        : failed ? <div className="dh-map-state" role="alert"><h3>Map unavailable</h3><p>{authError ? "Google Maps is not authorized for this site. Your administrator can check the map configuration." : "The map could not load. Try again."} The saved coordinates are still available in GPS points.</p>{!authError ? <button type="button" className="dh-button" onClick={async () => { const refreshed = await config.refetch(); if (refreshed.data?.api_key) { await library.refetch(); setAttempt(value => value + 1); } }}>Retry map</button> : null}</div>
          : !library.data ? <div className="dh-map-state" role="status">Loading map…</div> : null}
    </div>
    <div className="dh-map-detail" aria-live="polite">{selected ? <><strong>{selected.label}</strong><span>{displayTime(selected.time, history.time_zone)} IST · {selected.latitude.toFixed(6)}, {selected.longitude.toFixed(6)}{selected.accuracy != null ? ` · Accuracy ±${Math.round(selected.accuracy)} m` : ""}</span></> : <span>Select a marker or GPS point to inspect its time and coordinates.</span>}</div>
  </div>;
}
